import React from "react";
import StatusCard from "./StatusCard";
import { convertUtcToIst } from "../../util/Util"; // Adjust path as necessary

interface DeviceStatusProps {
  screenLockEnabled: boolean;
  screenLockTimeout: number;
  diskEncrypted: boolean;
  lastUpdated: string;
  antivirusEnabled?: boolean;
  windowsAutoUpdateEnabled?: boolean;
  windowsUpdatesPending?: number;
  windowsLastUpdateDate?: string;
  disabledBanner?: React.ReactNode;
  osName?: string;
  osVersion?: string;
}

const DeviceStatus: React.FC<DeviceStatusProps> = ({
  screenLockEnabled,
  screenLockTimeout,
  diskEncrypted,
  lastUpdated,
  antivirusEnabled,
  windowsAutoUpdateEnabled,
  windowsUpdatesPending,
  windowsLastUpdateDate,
  disabledBanner,
  osName,
  osVersion
}) => {
  // Format timeout to human-readable format
  const formatTimeout = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  // Helper function to ensure API timestamps are explicitly treated as UTC
  // by appending the missing "Z" timezone indicator.
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ensureUtc = (timeStr: any) => {
    if (typeof timeStr === "string" && !timeStr.endsWith("Z")) {
      return `${timeStr}Z`;
    }
    return timeStr;
  };

  return (
    <div className="space-y-4 px-2">
      {/* Header - Banner is now next to the title */}
      <div className="flex items-center w-full mb-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white shrink-0">
          Device Security Status
        </h2>
        {disabledBanner && (
          <div className="flex-1 flex justify-center px-4 min-w-0">
            {disabledBanner}
          </div>
        )}
      </div>

      {/* Status Cards Grid - Strictly enforced 5 columns to prevent any row wrapping */}
      <div className="grid grid-cols-5 gap-3 w-full">
        {/* Screen Lock Card */}
        <StatusCard
          title="Screen Lock"
          enabled={screenLockEnabled}
          borderColor="border-indigo-200"
          bgColor="bg-indigo-100"
          iconColor="text-indigo-600"
          icon={
            <svg
              className="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          }
          additionalInfo={
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 min-w-0">
              <div className="flex justify-between items-center gap-1">
                <span className="text-[11px] text-gray-600 dark:text-gray-300 truncate">Timeout:</span>
                <span className="text-[11px] font-semibold text-gray-900 dark:text-white shrink-0">
                  {formatTimeout(screenLockTimeout)}
                </span>
              </div>
            </div>
          }
        />

        {/* Disk Encryption Card */}
        <StatusCard
          title="Disk Encryption"
          enabled={diskEncrypted}
          borderColor="border-purple-200"
          bgColor="bg-purple-100"
          iconColor="text-purple-600"
          enabledText="Encrypted"
          disabledText="Not Encrypted"
          icon={
            <svg
              className="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          }
        />

        {/* Antivirus Card */}
        <StatusCard
          title="Antivirus"
          enabled={antivirusEnabled}
          borderColor="border-green-200"
          bgColor="bg-green-100"
          iconColor="text-green-600"
          icon={
            <svg
              className="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
              />
            </svg>
          }
        />

        {/* OS Version Card - Text wrapping enabled */}
        <StatusCard
          title="OS Version"
          borderColor="border-teal-200"
          bgColor="bg-teal-100"
          iconColor="text-teal-600"
          icon={
            <svg
              className="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          }
          additionalInfo={
            <div className="space-y-1.5 min-w-0">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 min-w-0">
                <div className="flex justify-between items-start gap-1">
                  <span className="text-[11px] text-gray-600 dark:text-gray-300 shrink-0">Name:</span>
                  <span className="text-[11px] font-semibold text-gray-900 dark:text-white text-right break-words min-w-0">
                    {osName || "N/A"}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 min-w-0">
                <div className="flex justify-between items-start gap-1">
                  <span className="text-[11px] text-gray-600 dark:text-gray-300 shrink-0">Ver:</span>
                  <span className="text-[11px] font-semibold text-gray-900 dark:text-white text-right break-words min-w-0">
                    {osVersion || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          }
        />

        {/* Windows Update Card */}
        <StatusCard
          title="OS Auto Update"
          enabled={windowsAutoUpdateEnabled}
          borderColor="border-blue-200"
          bgColor="bg-blue-100"
          iconColor="text-blue-600"
          icon={
            <svg
              className="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          }
          additionalInfo={
            <div className="space-y-1.5 min-w-0">
              {windowsUpdatesPending !== undefined && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 min-w-0">
                  <div className="flex justify-between items-center gap-1">
                    <span className="text-[11px] text-gray-600 dark:text-gray-300 truncate">
                      Pending:
                    </span>
                    <span
                      className={`text-[11px] font-semibold shrink-0 ${windowsUpdatesPending > 0
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-green-600 dark:text-green-400"
                        }`}
                    >
                      {windowsUpdatesPending > 0
                        ? `${windowsUpdatesPending}`
                        : "Up to Date"}
                    </span>
                  </div>
                </div>
              )}
              {windowsLastUpdateDate && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 min-w-0">
                  <div className="flex justify-between items-center gap-1">
                    <span className="text-[11px] text-gray-600 dark:text-gray-300 truncate">Last:</span>
                    <span className="text-[11px] font-semibold text-gray-900 dark:text-white shrink-0">
                      {convertUtcToIst(ensureUtc(windowsLastUpdateDate)).date}
                    </span>
                  </div>
                </div>
              )}
            </div>
          }
        />
      </div>

      {/* Last Checked Info */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 mt-4">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <svg
            className="w-4 h-4 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-xs">
            Last updated:{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {convertUtcToIst(ensureUtc(lastUpdated)).full}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default DeviceStatus;